import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing, Send, AlertTriangle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtDate, expiryBadge } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Reminders() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);

  const load = () => api.get("/reminders").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const requestRenewal = async (d, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/documents/${d.id}/request-renewal`);
      toast.success(`Renewal emailed to ${data.sent_to.join(", ")}`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="overline text-accent flex items-center gap-1.5"><BellRing className="h-3.5 w-3.5" />Automation</p>
        <h1 className="font-head text-3xl font-extrabold tracking-tight mt-1">Compliance Reminders</h1>
        <p className="text-muted-foreground text-sm mt-1">Documents expiring within 30 days, or already expired. Nudges surface at 30 / 15 / 7 days.</p>
      </div>

      {items.length === 0 ? (
        <div className="p-12 text-center bg-white border border-border rounded-md">
          <AlertTriangle className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">All compliance documents are valid. Nothing to chase.</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-md divide-y divide-border">
          {items.map((d) => {
            const b = expiryBadge(d.expiry_status);
            return (
              <div key={d.id} data-testid={`reminder-${d.id}`} onClick={() => nav(`/properties/${d.property_id}`)} className="px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-secondary/40 transition-colors">
                <div className={`h-10 w-10 rounded-md flex items-center justify-center ${d.expiry_status === "expired" ? "bg-[#9E382A]/12 text-[#9E382A]" : "bg-[#D98324]/12 text-[#D98324]"}`}>
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{d.doc_type}</p>
                  <p className="text-xs text-muted-foreground truncate">{d.property_title}</p>
                </div>
                <div className="text-right">
                  <span className={`text-[0.65rem] px-2 py-0.5 rounded-full border ${b.cls}`}>{b.label}</span>
                  <p className="text-xs text-muted-foreground mt-1">
                    {d.days_remaining < 0 ? `${Math.abs(d.days_remaining)}d overdue` : `${d.days_remaining}d left`} · {fmtDate(d.expiry_date)}
                  </p>
                </div>
                <Button size="sm" variant="outline" data-testid={`renew-btn-${d.id}`} onClick={(e) => requestRenewal(d, e)}>
                  <Send className="h-3.5 w-3.5 mr-1" />Request Renewal
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
